import { useEffect, useRef, useState } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import type { Paged, ProblemCard, SolutionCard } from '@/shared/lib/apiTypes'
import { HomeHeader } from '@/shared/ui/AppHeader'
import { ChevronRightIcon } from '@/shared/ui/icons/ChevronRightIcon'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { formatDate } from '@/shared/lib/format'
import { HomeHero } from '@/public/features/home/ui/HomeHero'
import { EmptyStateCard } from '@/shared/ui/EmptyState'
import { EMPTY_ORG_CARD, EMPTY_PROBLEM_CARD } from '@/public/features/home/constants/homeEmpty'
import { ProblemIssueCard, SolutionListCard } from '@/shared/ui/IssueCard'
import { countsById } from '@/shared/lib/cardCounts'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { profileTone } from '@/shared/lib/profileTone'
import { MOBILE_CARD_LIMIT } from '@/public/features/home/constants/homeCards'
import { CARD_CATEGORY_MAX } from '@/shared/constants/cards'
import { CARD_SHELL_CLASS } from '@/shared/constants/cards'
import { ISSUE_ICONS } from '@/public/features/home/constants/homeCards'

type Props = {
  problems: UseQueryResult<Paged<ProblemCard>>
  cards: UseQueryResult<Paged<SolutionCard>>
}

export function MobileHome({ problems, cards }: Props) {
  const problemItems = problems.data?.items ?? []
  const problemCounts = countsById(problemItems, (p) => p.matchedSolutionCount)

  const revealSentinel = useRef<HTMLDivElement>(null)
  const revealed = usePassedTop(revealSentinel)

  return (
    <div className="mobile-screen pc:hidden relative overflow-hidden">
      <AuroraBackground />

      <div className="relative z-[1]">
        <HomeHeader />
        <HomeHero />

        <div className="h-2 bg-violet-50" aria-hidden="true" />

        <div ref={revealSentinel} className="h-0" aria-hidden="true" />

        <HomeSection
          title="최근 등록된 시민 문제"
          moreTo="/problems"
          query={problems}
          empty={<EmptyStateCard {...EMPTY_PROBLEM_CARD} />}
          className="px-4 pt-2.5 pb-6"
        >
          {(items, labels) =>
            // 조회는 6장이지만 **모바일은 3장**이다(사용자 지시 2026-08-31 — 시안 831:30628).
            items.slice(0, MOBILE_CARD_LIMIT).map((p, i) => {
              const count = problemCounts.get(p.id)
              return (
                <li key={p.id}>
                  <Link className="block no-underline" to={`/problems/${p.id}`}>
                    <ProblemIssueCard
                      categories={(p.categoryCodes ?? [])
                        .slice(0, CARD_CATEGORY_MAX)
                        .map(labels.categoryLabel)}
                      title={p.title}
                      summary={p.summary ?? undefined}
                      icon={<IssueIcon index={i} />}
                      metaItems={[formatDate(p.createdAt), p.region.name]}
                      {...(count !== undefined ? { actionText: `적합카드 ${count}장`, actionMuted: count === 0 } : {})}
                    />
                  </Link>
                </li>
              )
            })
          }
        </HomeSection>

        <div className="h-2 bg-violet-50" aria-hidden="true" />

        <HomeSection
          title="최근 등록된 해결카드"
          moreTo="/solutions"
          query={cards}
          empty={<EmptyStateCard {...EMPTY_ORG_CARD} />}
          className="p-4"
        >
          {(items, labels) =>
            items.slice(0, MOBILE_CARD_LIMIT).map((c) => {
              return (
                <li key={c.id}>
                  <Link
                    className={`flex ${CARD_SHELL_CLASS} text-inherit no-underline`}
                    to={`/solutions/${c.id}`}
                  >
                    <SolutionListCard
                      profileIcon={<CityIcon size={20} />}
                      profileTone={profileTone(c.org.id)}
                      orgName={c.org.name}
                      categories={c.categoryCodes
                        .slice(0, CARD_CATEGORY_MAX)
                        .map(labels.categoryLabel)}
                      title={c.title}
                      summary={c.problemSolved}
                      {...(c.accessCondition !== null && c.accessCondition.length > 0
                        ? { notice: c.accessCondition, noticeIcon: <InfoIcon /> }
                        : {})}
                      metaItems={[]}
                    />
                  </Link>
                </li>
              )
            })
          }
        </HomeSection>
      </div>

      {revealed && <RevealedHeader />}
    </div>
  )
}

function RevealedHeader() {
  return (
    <div className="fixed inset-x-0 top-0 z-20 animate-fade-in border-b border-white-300 bg-white-50">
      <HomeHeader />
    </div>
  )
}

function usePassedTop(ref: React.RefObject<HTMLDivElement | null>): boolean {
  const [passed, setPassed] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (el === null) return
    const io = new IntersectionObserver(
      ([entry]) => setPassed(entry !== undefined && !entry.isIntersecting),
      { threshold: 1 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [ref])

  return passed
}

export function IssueIcon({ index }: { index: number }) {
  return (
    <img
      className="h-14 w-[60px] object-contain"
      src={ISSUE_ICONS[index % ISSUE_ICONS.length]}
      alt=""
    />
  )
}

const BLOBS = [
  { left: -78.14, top: -356.75, w: 400, h: 420, token: '--violet-500', alpha: 55, blur: 65 },
  { left: 152, top: -68, w: 350, h: 350, token: '--violet-500', alpha: 52, blur: 70 },
  { left: 201.86, top: -316.75, w: 300, h: 300, token: '--yellow-500', alpha: 50, blur: 60 },
  { left: 51.86, top: -76.75, w: 340, h: 340, token: '--yellow-50', alpha: 73, blur: 50 },
  { left: 81.86, top: 93.25, w: 280, h: 280, token: '--violet-50', alpha: 92, blur: 45 },
  { left: -8.14, top: 43.25, w: 250, h: 250, token: '--yellow-500', alpha: 20, blur: 50 },
]

function AuroraBackground() {
  return (
    <div
      className="absolute inset-x-0 top-[-44px] h-[336px] overflow-hidden bg-linear-to-b from-violet-50 from-0% to-transparent to-[89.683%]"
      aria-hidden="true"
    >
      {BLOBS.map((b, i) => (
        <span
          key={i}
          className="absolute block rounded-full"
          style={{
            left: b.left,
            top: b.top,
            width: b.w,
            height: b.h,
            background: `color-mix(in srgb, var(${b.token}) ${b.alpha}%, transparent)`,
            filter: `blur(${b.blur}px)`,
          }}
        />
      ))}

      <img
        className="pointer-events-none absolute top-[113px] left-[163.63px] h-[154.887px] w-[232.33px] opacity-70"
        src="/illust/home-hero-scene.png"
        alt=""
      />

      <div className="absolute inset-x-0 top-[284px] h-[52px] bg-linear-to-b from-transparent to-white-50" />
    </div>
  )
}

function HomeSection<T>({
  title,
  moreTo,
  query,
  empty,
  className,
  children,
}: {
  title: string
  moreTo: string
  query: UseQueryResult<Paged<T>>
  empty: React.ReactNode
  className: string
  children: (items: T[], labels: ReturnType<typeof useCardLabels>) => React.ReactNode
}) {
  const labels = useCardLabels()
  const items = query.data?.items ?? []
  const isEmpty = items.length === 0

  return (
    <section className={`flex flex-col gap-3 ${className}`}>
      <div className="flex items-center gap-3.5">
        <h2 className="m-0 min-w-0 flex-1 shrink-0 grow basis-0 text-body-lg leading-[1.4] font-semibold text-black-600">
          {title}
        </h2>
        {!isEmpty && (
          // 34×34 · radius 8 · chevron-right 24(black/300). 아이콘만이라 이름을 붙여 준다.
          <Link
            className="ui-pressable flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg text-black-300"
            to={moreTo}
            aria-label={`${title} 더보기`}
          >
            <ChevronRightIcon />
          </Link>
        )}
      </div>
      <QueryState isPending={query.isPending} error={query.error} isEmpty={isEmpty} empty={empty}>
        <ul className="m-0 flex list-none flex-col gap-4 p-0">{children(items, labels)}</ul>
      </QueryState>
    </section>
  )
}
